import {
  Box,
  Button,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

interface BaseFormProps<T> {
  values: Partial<T>;
  setValues: React.Dispatch<React.SetStateAction<Partial<T>>>;
  isEditing: boolean;
  /** Formulário só para consulta: Form deve desabilitar seus próprios campos. */
  readOnly: boolean;
}

interface CrudModalProps<T, P = {}> {
  open: boolean;
  onClose: () => void;
  title: string;
  onSubmit: (values: Partial<T>) => void | Promise<void>;
  initialData?: T | null;
  Form: React.ComponentType<BaseFormProps<T> & P>;
  extraFormProps?: P;
  submitLabel?: string;
  /** Sem botão Salvar nem submissão — só o Form (já desabilitado) e um Fechar. */
  readOnly?: boolean;
}

export function CrudModal<T, P = {}>({
  open,
  onClose,
  title,
  onSubmit,
  initialData,
  Form,
  extraFormProps,
  submitLabel = "Salvar",
  readOnly = false,
}: CrudModalProps<T, P>) {
  const [values, setValues] = useState<Partial<T>>({});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(values);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      setValues(initialData ?? {});
    }
  }, [open, initialData]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(61, 58, 52, 0.55)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",

          width: { xs: "95%", sm: 600, md: 800 },
          maxHeight: { xs: "95vh", sm: "90vh" },
          overflowY: "auto",

          bgcolor: "background.paper",
          borderRadius: { xs: 3, sm: 4 },
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          p: { xs: 2.5, sm: 4 },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: 'text.primary', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {title}
        </Typography>

        <Stack spacing={2}>
          <Form
            values={values}
            setValues={setValues}
            isEditing={!!initialData}
            readOnly={readOnly}
            {...(extraFormProps as P)}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
          {readOnly ? (
            <Button fullWidth variant="outlined" onClick={onClose}>
              Fechar
            </Button>
          ) : (
            <>
              <Button fullWidth variant="outlined" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={saving}
              >
                {submitLabel}
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Modal>
  );
}
